import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, RotateCw, AlertTriangle, Package, CalendarClock, Flame, Bookmark, CheckCircle2, XCircle, Tag, Briefcase, UserPlus, ListChecks, MessageCircle, Star, Gift, FileText } from "lucide-react";
import { timeAgo } from "@/lib/format";

/**
 * Notifications bell for the Header — hidden when logged out.
 * Feeds (merged, newest first):
 *   1. Classifieds: GET /classifieds/notifications (+ /read {ids}).
 *   2. Iter-612 shared feed: GET /notifications (rows {id,type,title,
 *      body,link,read,created_at}; per-item /notifications/{id}/read;
 *      /notifications/read-all) — chat inquiries, review approvals,
 *      unfinished check-ins, 48h review-window reminders.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();

  const q = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      const [oldRes, sharedRes] = await Promise.allSettled([
        sharedApi.get("/classifieds/notifications"),
        sharedApi.get("/notifications"),
      ]);
      const oldList =
        oldRes.status === "fulfilled"
          ? oldRes.value.data?.notifications || oldRes.value.data?.items || []
          : [];
      const rawShared =
        sharedRes.status === "fulfilled"
          ? (Array.isArray(sharedRes.value.data) ? sharedRes.value.data : sharedRes.value.data?.notifications) || []
          : [];
      const sharedList = rawShared.map((n) => ({ ...n, kind: n.type, feed: "shared" }));
      const list = [...oldList, ...sharedList].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      );
      return { list, unread_count: list.filter((n) => !n.read).length };
    },
    enabled: !!user,
    // Iter 684 — owner must see new pending reviews fast: 30 s → 15 s.
    refetchInterval: 15_000,
    retry: 0,
  });

  const readMut = useMutation({
    mutationFn: async (n) =>
      n.feed === "shared"
        ? sharedApi.post(`/notifications/${n.id}/read`)
        : sharedApi.post("/classifieds/notifications/read", { ids: [n.id] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", user?.id] }),
    onError: () => { /* silent — the click already navigates */ },
  });

  const readAllMut = useMutation({
    mutationFn: async () =>
      Promise.allSettled([
        sharedApi.post("/classifieds/notifications/read", { ids: null }),
        sharedApi.post("/notifications/read-all"),
      ]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", user?.id] }),
    onError: () => { /* silent */ },
  });

  if (!user) return null;

  const list = q.data?.list || [];
  const unread = q.data?.unread_count || 0;

  const onOpen = (n) => {
    if (!n.read) readMut.mutate(n);
    const url = destinationFor(n);
    if (url) nav(url);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="header-notifications-bell"
          className="relative p-2 rounded-full text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Notifications"
          title="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--brand-orange)] text-white text-[10px] font-bold grid place-items-center"
              data-testid="header-notifications-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[70vh] overflow-y-auto p-0" data-testid="header-notifications-menu">
        <div className="px-4 py-3 border-b border-soft flex items-center justify-between gap-3">
          <div className="font-display font-bold text-slate-900">Notifications</div>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => readAllMut.mutate()}
              data-testid="header-notifications-mark-all"
              className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>

        {q.isLoading && (
          <div className="p-8 text-center text-xs text-slate-400">Loading…</div>
        )}
        {!q.isLoading && q.isError && (
          <div className="p-6 text-center text-xs text-slate-500">
            Couldn&apos;t load notifications.
            <button
              type="button"
              onClick={() => q.refetch()}
              className="ml-1 text-[color:var(--brand-blue)] font-semibold hover:underline"
            >
              Retry
            </button>
          </div>
        )}
        {!q.isLoading && !q.isError && list.length === 0 && (
          <div className="p-8 text-center" data-testid="header-notifications-empty">
            <Bell className="w-8 h-8 mx-auto text-slate-300" />
            <div className="mt-2 text-sm font-semibold text-slate-700">You&apos;re all caught up</div>
            <div className="text-xs text-slate-400">We&apos;ll ping you when something new arrives.</div>
          </div>
        )}
        {list.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onOpen(n)}
            data-testid={`header-notification-${n.id}`}
            className={`w-full text-left px-4 py-3 border-b border-soft hover:bg-slate-50 transition-colors flex gap-3 ${n.read ? "opacity-70" : ""}`}
          >
            <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={iconBg(n.kind, n.meta)}>
              <KindIcon kind={n.kind} meta={n.meta} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className={`text-sm ${n.read ? "text-slate-700" : "font-bold text-slate-900"} truncate`}>{n.title || defaultTitle(n.kind, n.meta)}</div>
                {!n.read && <span className="w-2 h-2 mt-1.5 rounded-full bg-[color:var(--brand-orange)] shrink-0" aria-label="Unread" />}
              </div>
              {n.body && <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">{n.body}</div>}
              <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">{timeAgo(n.created_at)}</div>
            </div>
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function destinationFor(n) {
  if (!n) return null;
  // Iter-626 §4 — the owner's daily digest opens the status panel.
  if (n.kind === "owner_daily_digest") return "/partner-program";
  // Iter-600/612 — the shared feed carries `link` values in the backend
  // pod's reference-frontend routes. Translate them to OUR routes.
  if (n.link) {
    const [path, query] = String(n.link).split("?");
    const qs = query ? `?${query}` : "";
    if (path === "/account/reward-coins" || path === "/wallet") return `/rewards${qs}`;
    if (path.startsWith("/classified-chat/")) return `/chat/${path.split("/")[2]}${qs}`;
    if (path.startsWith("/vendor-dashboard")) return "/partner-program";
    if (path === "/engage-test/customer" || path.startsWith("/engage-test")) {
      // Iter-613 — resume deep-link needs OUR store QR (session payloads
      // don't carry it); the last claimed session persists it locally.
      const m = /(?:^|&)resume=([^&]+)/.exec(query || "");
      let qr = null;
      try {
        qr = JSON.parse(localStorage.getItem("engage_last_session") || "null")?.qr || null;
      } catch { qr = null; }
      if (m && qr) return `/engage/${qr}?resume=${m[1]}`;
      return "/my/visits";
    }
    return n.link;
  }
  if (n.action_url) return n.action_url;
  if (n.kind === "chat_message") return "/chat";
  if (n.kind === "engage_pending_review") return "/partner-program";
  if (
    n.kind === "engage_review_approved" ||
    n.kind === "engage_review_rejected" ||
    n.kind === "engage_session_unfinished" ||
    n.kind === "engage_review_window"
  ) {
    return "/my/visits";
  }
  if (n.kind === "points_expiry") return "/rewards";
  // Iter 730 — dealer stock-sheet reminder deep-links into the My Ads
  // "Update from stock sheet" dialog.
  if (n.kind === "stock_sheet_reminder") return "/my-ads?stock_sheet=1";
  if (n.kind === "renewal_due") return "/my-ads";
  if (n.kind === "duplicate_listing") {
    const id = n.meta?.listing_id;
    return id ? `/ad/${id}` : "/my-ads";
  }
  if (n.kind === "follow_new_listing" || n.kind === "saved_search_match") {
    const id = n.meta?.listing_id;
    return id ? `/ad/${id}` : "/";
  }
  if (n.kind === "event_approved") {
    const id = n.meta?.event_id;
    return id ? `/events/${id}` : "/my/events";
  }
  if (n.kind === "event_rejected") {
    return "/my/events";
  }
  if (n.kind === "deal_approved") {
    const id = n.meta?.deal_id;
    return id ? `/deals/${id}` : "/my/deals";
  }
  if (n.kind === "deal_rejected") {
    return "/my/deals";
  }
  // Safety-hold (iter-224): approval unlocks the public detail; rejection
  // routes the seller back to My Ads where the red banner explains.
  if (n.kind === "listing_approved") {
    const id = n.meta?.listing_id;
    return id ? `/ad/${id}` : "/my-ads";
  }
  if (n.kind === "listing_rejected") {
    return "/my-ads";
  }
  // Iter-263 jobs module (§9)
  if (n.kind === "job_application_submitted") {
    return "/jobs/applications";
  }
  if (n.kind === "job_new_applicant") {
    const lid = n.meta?.listing_id;
    return lid ? `/jobs/employer?listing=${lid}` : "/jobs/employer";
  }
  if (n.kind === "job_application_status") {
    const aid = n.meta?.application_id;
    return aid ? `/jobs/applications/${aid}` : "/jobs/applications";
  }
  if (n.kind === "job_applicant_digest") {
    return "/jobs/employer";
  }
  return null;
}

function defaultTitle(kind, meta) {
  // Iter-612 shared-feed types (rows usually carry their own title).
  if (kind === "chat_message") return "💬 New message about your ad";
  if (kind === "engage_pending_review") return "⭐ A review is waiting for your approval";
  if (kind === "engage_review_approved") return "✅ Your review was approved";
  if (kind === "engage_review_rejected") return "Your review wasn't approved this time";
  if (kind === "engage_session_unfinished") return "⏳ Finish your check-in";
  if (kind === "engage_review_window") return "⏳ Your 48h review window closes soon";
  if (kind === "owner_daily_digest") return "📊 Your day at a glance";
  // Iter 630/631 — admin goodwill grants (rows usually carry their own title).
  if (kind === "admin_points_grant") return "🎁 A gift from AllSale landed in your wallet";
  if (kind === "admin_points_grant_reversed") return "↩️ A points grant was reversed";
  if (kind === "points_expiry") return "⏳ Your reward coins expire soon";
  if (kind === "stock_sheet_reminder") return "Your stock sheet is 30+ days old";
  if (kind === "renewal_due") return meta?.final ? "Your ad ends today!" : "Your ad expires soon";
  if (kind === "duplicate_listing") return "Looks like a duplicate ad";
  if (kind === "follow_new_listing") return "New ad in a category you follow";
  if (kind === "saved_search_match") return "New match for your saved search";
  if (kind === "event_approved") return "Your event is live 🎉";
  if (kind === "event_rejected") return "Your event was not approved";
  if (kind === "deal_approved") return "Your deal is live 🎉";
  if (kind === "deal_rejected") return "Your deal was not approved";
  if (kind === "listing_approved") return "Your listing is now live 🎉";
  if (kind === "listing_rejected") return "Your listing was not approved";
  // Iter-263 jobs module (§9)
  if (kind === "job_application_submitted") return `Application sent — ${meta?.job_title || "your job application"}`;
  if (kind === "job_new_applicant") return `New applicant on ${meta?.job_title || "your job listing"}`;
  if (kind === "job_application_status") {
    const s = meta?.status;
    if (s === "viewed") return "Your application was viewed";
    if (s === "shortlisted") return "You've been shortlisted 🎉";
    if (s === "interviewing") return "Interview stage — nice work";
    if (s === "hired") return "You've been hired 🎉";
    if (s === "rejected") return "Your application wasn't successful";
    return "Application status updated";
  }
  if (kind === "job_applicant_digest") return `Weekly applicant summary${meta?.count ? ` — ${meta.count} new` : ""}`;
  return "Notification";
}

function iconBg(kind, meta) {
  // Iter-612 shared-feed types.
  if (kind === "chat_message") return { backgroundColor: "var(--brand-blue-wash)", color: "var(--brand-blue)" };
  if (kind === "engage_pending_review") return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  if (kind === "engage_review_approved") return { backgroundColor: "rgb(209 250 229)", color: "rgb(4 120 87)" };
  if (kind === "engage_review_rejected") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  if (kind === "engage_session_unfinished" || kind === "engage_review_window")
    return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  if (kind === "owner_daily_digest") return { backgroundColor: "rgb(224 242 254)", color: "rgb(2 132 199)" };
  // Iter 630/631 — admin goodwill grants.
  if (kind === "admin_points_grant") return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  if (kind === "admin_points_grant_reversed") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  if (kind === "points_expiry") return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  if (kind === "stock_sheet_reminder") return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  if (kind === "renewal_due" && meta?.final) return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  if (kind === "renewal_due") return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  if (kind === "duplicate_listing") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  if (kind === "saved_search_match") return { backgroundColor: "rgb(209 250 229)", color: "rgb(4 120 87)" };
  if (kind === "event_approved") return { backgroundColor: "rgb(209 250 229)", color: "rgb(4 120 87)" };
  if (kind === "event_rejected") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  if (kind === "deal_approved") return { backgroundColor: "rgb(209 250 229)", color: "rgb(4 120 87)" };
  if (kind === "deal_rejected") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  if (kind === "listing_approved") return { backgroundColor: "rgb(209 250 229)", color: "rgb(4 120 87)" };
  if (kind === "listing_rejected") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
  // Iter-263 jobs module (§9)
  if (kind === "job_application_submitted") return { backgroundColor: "var(--brand-blue-wash)", color: "var(--brand-blue)" };
  if (kind === "job_new_applicant") return { backgroundColor: "rgb(219 234 254)", color: "rgb(29 78 216)" };
  if (kind === "job_application_status") {
    const s = meta?.status;
    if (s === "hired" || s === "shortlisted") return { backgroundColor: "rgb(209 250 229)", color: "rgb(4 120 87)" };
    if (s === "rejected") return { backgroundColor: "rgb(254 226 226)", color: "rgb(185 28 28)" };
    if (s === "interviewing") return { backgroundColor: "rgb(224 231 255)", color: "rgb(67 56 202)" };
    return { backgroundColor: "var(--brand-blue-wash)", color: "var(--brand-blue)" };
  }
  if (kind === "job_applicant_digest") return { backgroundColor: "rgb(254 243 199)", color: "rgb(180 83 9)" };
  return { backgroundColor: "var(--brand-blue-wash)", color: "var(--brand-blue)" };
}

function KindIcon({ kind, meta }) {
  const cls = "w-4 h-4";
  // Iter-612 shared-feed types.
  if (kind === "chat_message") return <MessageCircle className={cls} />;
  if (kind === "engage_pending_review") return <Star className={cls} />;
  if (kind === "engage_review_approved") return <CheckCircle2 className={cls} />;
  if (kind === "engage_review_rejected") return <XCircle className={cls} />;
  if (kind === "engage_session_unfinished" || kind === "engage_review_window")
    return <CalendarClock className={cls} />;
  if (kind === "owner_daily_digest") return <ListChecks className={cls} />;
  // Iter 630/631 — admin goodwill grants.
  if (kind === "admin_points_grant") return <Gift className={cls} />;
  if (kind === "admin_points_grant_reversed") return <XCircle className={cls} />;
  if (kind === "points_expiry") return <CalendarClock className={cls} />;
  if (kind === "stock_sheet_reminder") return <FileText className={cls} />;
  if (kind === "renewal_due" && meta?.final) return <Flame className={cls} />;
  if (kind === "renewal_due") return <CalendarClock className={cls} />;
  if (kind === "duplicate_listing") return <AlertTriangle className={cls} />;
  if (kind === "follow_new_listing") return <Package className={cls} />;
  if (kind === "saved_search_match") return <Bookmark className={cls} />;
  if (kind === "event_approved") return <CheckCircle2 className={cls} />;
  if (kind === "event_rejected") return <XCircle className={cls} />;
  if (kind === "deal_approved") return <Tag className={cls} />;  if (kind === "deal_rejected") return <XCircle className={cls} />;
  if (kind === "listing_approved") return <CheckCircle2 className={cls} />;
  if (kind === "listing_rejected") return <XCircle className={cls} />;
  // Iter-263 jobs module (§9)
  if (kind === "job_application_submitted") return <Briefcase className={cls} />;
  if (kind === "job_new_applicant") return <UserPlus className={cls} />;
  if (kind === "job_application_status") {
    const s = meta?.status;
    if (s === "hired" || s === "shortlisted") return <CheckCircle2 className={cls} />;
    if (s === "rejected") return <XCircle className={cls} />;
    return <Briefcase className={cls} />;
  }
  if (kind === "job_applicant_digest") return <ListChecks className={cls} />;
  return <RotateCw className={cls} />;
}
